import React, { useEffect } from "react";
import { Column, Query } from "material-table";
import { useSnackbar } from "notistack";
import moment from "moment";
import { DefaultTable } from "components";
import { getMembersNameChanges, NameChange } from "api/admin/members";
import { useMounted } from "utils/hooks";
import i18n from "./i18n";

// prop types
interface NameChangesTabProps {
    clubId?: string,
}

// name changes table
const NameChangesTab = ({ clubId }: NameChangesTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<NameChange>) => {
        // paging
        const params: Parameters<typeof getMembersNameChanges>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // club filter
        params.clubId = clubId;

        return params;
    };

    // table ref for refresh
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    // table columns
    const tableColumns: Column<NameChange>[] = [
        {
            field: "createdAt",
            render: ({ createdAt }) => moment(createdAt).format("DD.MM.YYYY HH:mm"),
            title: i18n.date,
        },
        {
            field: "originalFullName",
            title: i18n.originalName,
        },
        {
            field: "newFullName",
            title: i18n.newName,
        },
    ];

    // render
    return (
        <DefaultTable
            columns={tableColumns}
            data={(query) => new Promise((resolve) => {
                const params = convertQueryToParams(query);
                getMembersNameChanges(params)
                    .then((response) => resolve({
                        ...response,
                        page: query.page,
                    }))
                    .catch((err) => {
                        displayError(err);
                        resolve({ data: [], page: 0, totalCount: 0 });
                    });
            })}
            options={{ search: false, sorting: false }}
            tableRef={tableRef}
            title={i18n.nameChanges}
        />
    );
};

export default NameChangesTab;
